<template>
    <div class="container">
        <div class="row mat32">
            <div class="col-12 title">Wersje językowe</div>
        </div>

        <div class="row mat24 flex-nowrap">
            <!-- Language sidebar -->
            <div class="side-bar none-mobile">
                <base-button-group>
                    <base-button
                        v-for="lang in languages"
                        :key="lang.code"
                        btn11
                        :class="{ 'is-active-lang': lang.code === currentLanguage }"
                        @click="goToLanguage(lang)"
                    >
                        <span class="lang-btn">
                            <span>{{ lang.label }} | {{ lang.code.toUpperCase() }}</span>
                            <i
                                class="fa fa-circle lang-status"
                                :class="isLanguageEnabled(lang.code) ? 'lang-status--on' : 'lang-status--off'"
                                aria-hidden="true"
                            />
                        </span>
                    </base-button>
                </base-button-group>
            </div>

            <div class="content-wrap">
                <!-- Mobile language picker -->
                <div class="mobile-lang-nav none-desktop mab16">
                    <base-button
                        v-for="lang in languages"
                        :key="`m-${lang.code}`"
                        btn11
                        short
                        class="mab8"
                        :class="{ 'is-active-lang': lang.code === currentLanguage }"
                        @click="goToLanguage(lang)"
                    >
                        {{ lang.code.toUpperCase() }}
                        <i
                            class="fa fa-circle lang-status"
                            :class="isLanguageEnabled(lang.code) ? 'lang-status--on' : 'lang-status--off'"
                            aria-hidden="true"
                        />
                    </base-button>
                </div>

                <div ref="saveFeedback">
                    <panel-info-box v-if="successMessage" class="mab16">
                        <p class="text-lg">
                            <b>{{ successMessage }}</b>
                        </p>
                    </panel-info-box>
                    <panel-info-box v-if="errorMessage" class="mab16">
                        <p class="text-lg">
                            <b>{{ errorMessage }}</b>
                        </p>
                    </panel-info-box>
                </div>

                <div v-if="loading" class="row mat16">
                    <div class="col-12">
                        <div class="admin-header__loading-state">
                            Trwa wczytywanie
                            <div class="mal15 lds-ellipsis">
                                <div />
                                <div />
                                <div />
                            </div>
                        </div>
                    </div>
                </div>

                <template v-else-if="form">
                    <panel-competition-form-box>
                        <div class="lang-header">
                            <p class="lang-header__title">{{ currentLanguageTitle }}</p>
                            <div class="lang-header__toggle">
                                <panel-input-switch
                                    :value="languageEnabled"
                                    name="languageEnabled"
                                    label=""
                                    :options="toggleOptions"
                                    @input="onToggle"
                                />
                            </div>
                        </div>
                    </panel-competition-form-box>

                    <panel-competition-languages-form v-model="form" />

                    <div class="actions-wrap">
                        <base-button btn1 short :disabled="saving" @click="save">
                            <span v-if="!saving">Zapisz</span>
                            <span v-else>Zapisywanie...</span>
                        </base-button>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex';
    import { READ_PANEL_COMPETITION } from '@/store/actions.type';
    import { getTranslations, updateTranslations, updateLanguageToggle } from '@/api/translations';
    import PanelCompetitionFormBox from '@/components/PanelCompetitionFormBox';
    import PanelCompetitionLanguagesForm from '@/components/PanelCompetitionLanguagesForm';
    import PanelInputSwitch from '@/components/PanelInputSwitch';
    import PanelInfoBox from '@/components/PanelInfoBox';

    const LANGUAGES = [
        { code: 'en', label: 'angielski', route: 'panel-competition-languages', settingKey: 'enOn' },
        { code: 'cz', label: 'czeski', route: 'panel-competition-languages-cz', settingKey: 'czOn' },
        { code: 'de', label: 'niemiecki', route: 'panel-competition-languages-de', settingKey: 'deOn' },
        { code: 'fr', label: 'francuski', route: 'panel-competition-languages-fr', settingKey: 'frOn' },
        { code: 'lt', label: 'litewski', route: 'panel-competition-languages-lt', settingKey: 'ltOn' },
        { code: 'ua', label: 'ukraiński', route: 'panel-competition-languages-ua', settingKey: 'UaOn' },
    ];

    const ROUTE_TO_LANG = {
        'panel-competition-languages': 'en',
        'panel-competition-languages-cz': 'cz',
        'panel-competition-languages-de': 'de',
        'panel-competition-languages-fr': 'fr',
        'panel-competition-languages-lt': 'lt',
        'panel-competition-languages-ua': 'ua',
    };

    const LANG_TITLES = {
        en: 'Język angielski',
        cz: 'Język czeski',
        de: 'Język niemiecki',
        fr: 'Język francuski',
        lt: 'Język litewski',
        ua: 'Język ukraiński',
    };

    function cloneJson(value) {
        return JSON.parse(JSON.stringify(value));
    }

    function asObject(value) {
        if (!value || typeof value !== 'object' || Array.isArray(value)) {
            return {};
        }
        return value;
    }

    function asString(value) {
        return value == null ? '' : value;
    }

    function asArray(value) {
        return Array.isArray(value) ? value : [];
    }

    function padArray(list, length) {
        const result = asArray(list).slice();
        while (result.length < length) {
            result.push('');
        }
        return result;
    }

    function emptyForm() {
        return {
            language: '',
            competition: {
                base: {
                    description: '',
                    remarks: '',
                    website: '',
                    registerMailContent: '',
                    statuteLink: '',
                    statementContents: [],
                },
                translation: {
                    description: '',
                    remarks: '',
                    website: '',
                    registerMailContent: '',
                    statuteLink: '',
                    statementContents: [],
                },
            },
            cycle: null,
            classifications: [],
            inputs: [],
        };
    }

    function normalizeNamedItem(item) {
        const result = asObject(item);
        result.base = asObject(result.base);
        result.translation = asObject(result.translation);
        result.translation.name = asString(result.translation.name);
        return result;
    }

    // Keep GET shape 1:1 for PUT. Only fill missing translation objects so v-model is safe.
    function normalizeTranslations(data) {
        const payload = cloneJson(data || emptyForm());
        payload.competition = asObject(payload.competition);
        payload.competition.base = asObject(payload.competition.base);
        payload.competition.translation = asObject(payload.competition.translation);

        const transComp = payload.competition.translation;
        ['description', 'remarks', 'website', 'registerMailContent', 'statuteLink'].forEach((field) => {
            transComp[field] = asString(transComp[field]);
        });

        const baseStatements = asArray(payload.competition.base.statementContents);
        payload.competition.base.statementContents = baseStatements;
        payload.competition.translation.statementContents = padArray(
            transComp.statementContents,
            baseStatements.length
        );

        if (payload.cycle) {
            payload.cycle = normalizeNamedItem(payload.cycle);
        }

        payload.classifications = asArray(payload.classifications).map((classification) => {
            const next = normalizeNamedItem(classification);
            next.specialCategories = asArray(next.specialCategories).map(normalizeNamedItem);
            return next;
        });

        payload.inputs = asArray(payload.inputs).map((input) => {
            const next = asObject(input);
            next.base = asObject(next.base);
            next.translation = asObject(next.translation);
            if (next.type === 'fc') {
                next.translation.description = asString(next.translation.description);
            } else if (next.type === 'fs' || next.type === 'fr') {
                next.translation.name = asString(next.translation.name);
                next.base.options = asArray(next.base.options);
                next.translation.options = padArray(next.translation.options, next.base.options.length);
            } else {
                next.translation.name = asString(next.translation.name);
            }
            return next;
        });

        return payload;
    }

    export default {
        name: 'PanelCompetitionLanguages',
        components: {
            PanelCompetitionFormBox,
            PanelCompetitionLanguagesForm,
            PanelInputSwitch,
            PanelInfoBox,
        },
        props: {
            competition: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                languages: LANGUAGES,
                loading: true,
                saving: false,
                toggling: false,
                form: null,
                successMessage: '',
                errorMessage: '',
                toggleOptions: [
                    { value: true, title: 'Aktywny' },
                    { value: false, title: 'Nieaktywny' },
                ],
            };
        },
        computed: {
            ...mapGetters(['competitionsSettings']),
            competitionId() {
                return this.competition.id;
            },
            competitionSetting() {
                return this.competitionsSettings[this.competitionId] || {};
            },
            currentLanguage() {
                return ROUTE_TO_LANG[this.$route.name] || 'en';
            },
            currentLanguageMeta() {
                return this.languages.find((l) => l.code === this.currentLanguage) || this.languages[0];
            },
            currentLanguageTitle() {
                return LANG_TITLES[this.currentLanguage] || 'Język';
            },
            languageEnabled() {
                const key = this.currentLanguageMeta.settingKey;
                return Boolean(this.competitionSetting[key]);
            },
        },
        watch: {
            currentLanguage: {
                immediate: true,
                handler() {
                    this.loadTranslations();
                },
            },
        },
        methods: {
            ...mapActions({
                readPanelCompetition: READ_PANEL_COMPETITION,
            }),
            isLanguageEnabled(code) {
                const lang = this.languages.find((l) => l.code === code);
                if (!lang) return false;
                return Boolean(this.competitionSetting[lang.settingKey]);
            },
            goToLanguage(lang) {
                if (lang.code === this.currentLanguage) return;
                this.$router.push({
                    name: lang.route,
                    params: this.competition,
                    alias: true,
                });
            },
            clearMessages() {
                this.successMessage = '';
                this.errorMessage = '';
            },
            async loadTranslations() {
                if (!this.competitionId) return;
                this.loading = true;
                this.clearMessages();
                try {
                    const data = await getTranslations(this.competitionId, this.currentLanguage);
                    this.form = normalizeTranslations(data);
                } catch (error) {
                    this.form = emptyForm();
                    this.errorMessage = 'Nie udało się wczytać tłumaczeń.';
                } finally {
                    this.loading = false;
                }
            },
            async onToggle(enabled) {
                if (this.toggling) return;
                const key = this.currentLanguageMeta.settingKey;
                const previous = Boolean(this.competitionSetting[key]);
                const next = enabled === true || enabled === 'true' || enabled === 1 || enabled === '1';
                if (next === previous) return;

                this.toggling = true;
                this.clearMessages();
                if (this.competitionSetting) {
                    this.$set(this.competitionSetting, key, next);
                }
                try {
                    await updateLanguageToggle(this.competitionId, { [key]: next });
                    try {
                        await this.readPanelCompetition(this.competitionId);
                    } catch (_refreshError) {
                        // Settings already updated locally; refresh is best-effort.
                    }
                } catch (error) {
                    if (this.competitionSetting) {
                        this.$set(this.competitionSetting, key, previous);
                    }
                    this.errorMessage = 'Nie udało się zmienić statusu języka.';
                } finally {
                    this.toggling = false;
                }
            },
            scrollToFeedback() {
                this.$nextTick(() => {
                    const el = this.$refs.saveFeedback;
                    if (el && typeof el.scrollIntoView === 'function') {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                });
            },
            async save() {
                if (!this.form || this.saving) return;
                this.saving = true;
                this.clearMessages();
                try {
                    await updateTranslations(
                        this.competitionId,
                        this.currentLanguage,
                        cloneJson(this.form)
                    );
                    this.successMessage = 'Tłumaczenia zostały zapisane.';
                } catch (error) {
                    this.errorMessage = 'Nie udało się zapisać tłumaczeń.';
                } finally {
                    this.saving = false;
                    this.scrollToFeedback();
                }
            },
        },
    };
</script>

<style scoped>
    .title {
        font-size: 24px;
        font-weight: 600;
    }

    .side-bar {
        width: 200px;
        flex-shrink: 0;
    }

    .content-wrap {
        flex: 1;
        min-width: 0;
    }

    .lang-btn {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        gap: 8px;
    }

    .lang-status {
        font-size: 10px;
    }

    .lang-status--on {
        color: #50ae55;
    }

    .lang-status--off {
        color: #ff1744;
    }

    .side-bar >>> .is-active-lang.btn--11,
    .mobile-lang-nav >>> .is-active-lang.btn--11,
    .is-active-lang {
        background-color: #455a64 !important;
        border: none !important;
        box-shadow: none !important;
        outline: none !important;
    }

    .side-bar >>> .is-active-lang.btn--11:hover,
    .mobile-lang-nav >>> .is-active-lang.btn--11:hover {
        background-color: #455a64 !important;
    }

    .lang-header {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        width: 100%;
    }

    .lang-header__title {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
    }

    .lang-header__toggle {
        width: 100%;
        max-width: 280px;
    }

    .actions-wrap {
        display: flex;
        justify-content: flex-end;
        margin-top: 16px;
        margin-bottom: 64px;
    }

    .mobile-lang-nav {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }
    .lang-header__toggle >>> input[type='radio'] + label::before {
        background-color: #546e7a;
    }

    @media (min-width: 992px) {
        .content-wrap {
            margin-left: 30px;
        }
    }
</style>
